import POSResponseData from 'features/POSRegisters/interfaces/POSResponseData';
import POSState from 'features/POSRegisters/interfaces/POSState';
import apiAlertToNposAlertConverter from './apiAlertToNposAlertConverter';
import zuluToLocaleTimeConverter from './zuluToLocaleTimeConverter';
import Register from '../interfaces/Register';
import Device from '../interfaces/Device';
import ScanDetails from '../interfaces/ScanDetails';
import DeviceType from '../interfaces/DeviceType';

//#region Constants
const NOT_APPLICABLE_STRING: string = 'Not Applicable';
const UNAVAILABLE_STRING: string = 'Unavailable';
const alertsToMute = ['', null, undefined];
//#endregion Constants

function switchInformationNormalizer<T extends Partial<ScanDetails>>(scanResults: T): T {
  const normalizedScanResults = Object.fromEntries(
    Object.entries(scanResults).map(([key, value]) => [
      key,
      value === null ? UNAVAILABLE_STRING : value,
    ])
  );
  const {vlanNumber, switchId} = normalizedScanResults;
  if (
    (vlanNumber === 'NotApplicable' && switchId === 'NotApplicable') ||
    (vlanNumber === 'N/A' && switchId === 'N/A')
  ) {
    return {
      ...scanResults,
      vlanNumber: NOT_APPLICABLE_STRING,
      switchId: NOT_APPLICABLE_STRING,
      switchNumber: NOT_APPLICABLE_STRING,
      portNumber: NOT_APPLICABLE_STRING,
    };
  } else if (vlanNumber === UNAVAILABLE_STRING && switchId === UNAVAILABLE_STRING) {
    return {
      ...scanResults,
      vlanNumber: UNAVAILABLE_STRING,
      switchId: UNAVAILABLE_STRING,
      switchNumber: UNAVAILABLE_STRING,
      portNumber: UNAVAILABLE_STRING,
    };
  } else {
    return scanResults;
  }
}

function nposApiNormalizer(apiData: POSResponseData): Partial<POSState> {
  const {
    networkCircuits = [],
    registers: ipads = [],
    consumerFacingDisplays: ipadsMini = [],
    receiptPrinters = [],
    paymentTerminals = [],
    cashDrawers = [],
    barcodeScanners = [],
  } = apiData;

  //#region NORMALIZATION
  const networkCircuitsNormalized = networkCircuits.map(device => {
    return {
      ...device,
      alerts: apiAlertToNposAlertConverter(device.alerts, DeviceType.NETWORK_CIRCUIT),
    };
  });

  const ipadNormalized = ipads.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {
        ...switchInformationNormalizer(scanResults),
        mobileApiLastSeen: zuluToLocaleTimeConverter(scanResults.mobileApiLastSeen),
        registerFirstSeenDate: zuluToLocaleTimeConverter(scanResults.registerFirstSeenDate),
        registerLastSeenDate: zuluToLocaleTimeConverter(scanResults.registerLastSeenDate),
      },
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.IPAD).filter(
        alert => !alertsToMute.includes(alert.alertDefinitionId)
      ),
    };
  });

  const ipadMiniNormalized = ipadsMini.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {
        ...switchInformationNormalizer(scanResults),
        mobileApiLastSeen: zuluToLocaleTimeConverter(scanResults.mobileApiLastSeen),
        consumerFacingDisplayFirstSeenDate: zuluToLocaleTimeConverter(
          scanResults.consumerFacingDisplayFirstSeenDate
        ),
        consumerFacingDisplayLastSeenDate: zuluToLocaleTimeConverter(
          scanResults.consumerFacingDisplayLastSeenDate
        ),
      },
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.IPAD_MINI).filter(
        alert => !alertsToMute.includes(alert.alertDefinitionId)
      ),
    };
  });

  const receiptPrintersNormalized = receiptPrinters.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {
        ...switchInformationNormalizer(scanResults),
        printerFirstSeenDate: zuluToLocaleTimeConverter(scanResults.printerFirstSeenDate),
        printerLastSeenDate: zuluToLocaleTimeConverter(scanResults.printerLastSeenDate),
      },
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.RECEIPT_PRINTER),
    };
  });

  const paymentTerminalsNormalized = paymentTerminals.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {
        ...switchInformationNormalizer(scanResults),
      },
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.PAYMENT_TERMINAL),
    };
  });

  const cashDrawersNormalized = cashDrawers.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {
        ...switchInformationNormalizer(scanResults),
      },
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.CASH_DRAWER),
    };
  });

  const barcodeScannersNormalized = barcodeScanners.map(device => {
    const {scanResults, alerts} = device;
    return {
      ...device,
      scanResults: {...switchInformationNormalizer(scanResults)},
      alerts: apiAlertToNposAlertConverter(alerts, DeviceType.BARCODE_SCANNER),
    };
  });
  //#endregion NORMALIZATION

  //#region GROUP_REGISTERS
  const registers: Record<string, Register> = {};
  const unsupportedDevices: Record<string, Device<ScanDetails>> = {};

  function associateDevice(device: Device<ScanDetails>, key: string) {
    const {scanResults} = device;
    const id = scanResults.associatedDevice!;

    if (!registers[id] && scanResults.isAssociated) registers[id] = {id} as Register;
    if (device && scanResults.isAssociated) registers[id][key] = device;

    // check if the device is not supported
    if (scanResults?.isSupported === false) {
      unsupportedDevices[key] = device;
    }
  }

  ipadNormalized.forEach(ipad => associateDevice(ipad, 'ipad'));
  ipadMiniNormalized.forEach(ipadMini => associateDevice(ipadMini, 'ipadMini'));
  receiptPrintersNormalized.forEach(receiptPrinter =>
    associateDevice(receiptPrinter, 'receiptPrinter')
  );
  barcodeScannersNormalized.forEach(barcodeScanner =>
    associateDevice(barcodeScanner, 'barcodeScanner')
  );
  cashDrawersNormalized.forEach(cashDrawer => associateDevice(cashDrawer, 'cashDrawer'));
  paymentTerminalsNormalized.forEach(paymentTerminal =>
    associateDevice(paymentTerminal, 'paymentTerminal')
  );

  Object.entries(unsupportedDevices).forEach(([key, device]) => {
    Object.entries(registers).forEach(([id, register]) => {
      if (register[key] === undefined || register[key]?.length === 0) {
        registers[id][key] = device;
      }
    });
  });

  const sortedRegisters = Object.values(registers).sort((a, b) => {
    const aId = a.ipad?.scanResults?.workstationNumber;
    const bId = b.ipad?.scanResults?.workstationNumber;
    if (aId && bId) {
      return String(aId).localeCompare(String(bId), undefined, {numeric: true});
    } else if (aId) {
      return -1;
    } else if (bId) {
      return 1;
    } else {
      return 0;
    }
  });

  //#endregion GROUP_REGISTERS

  return {
    ...apiData,
    networkCircuits: networkCircuitsNormalized,
    ipads: ipadNormalized,
    ipadsMini: ipadMiniNormalized,
    receiptPrinters: receiptPrintersNormalized,
    paymentTerminals: paymentTerminalsNormalized,
    cashDrawers: cashDrawersNormalized,
    barcodeScanners: barcodeScannersNormalized,
    registers: sortedRegisters,
  };
}

export default nposApiNormalizer;
